import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  Alert,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useApiSend } from '../utils/httpClientTs';
import {
  checkMembershipAction,
  executeMembershipAction,
  initiateUser,
} from '../endpoints/usersTs';
import {
  MembershipActionRequest,
  MembershipActionCheckResponse,
  InitiateUserRequest,
} from '../types/users';
import ChapterSelector from './ChapterSelector';
import DateTimePicker from './DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { CheckCircle } from '@mui/icons-material';

interface MemberActionModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  actionType: 'transfer' | 'reinstatement' | 'initiation'; // Determined by parent component
  onSuccess?: () => void;
}

type StepType = 'select' | 'check' | 'confirm' | 'success';

const MemberActionModal: React.FC<MemberActionModalProps> = ({
  open,
  onClose,
  userId,
  actionType,
  onSuccess,
}) => {
  // Step management
  const [activeStep, setActiveStep] = useState<StepType>('select');

  // Selection step
  const [chapterId, setChapterId] = useState<string>('');
  const [chapterError, setChapterError] = useState<boolean>(false);
  const [actionDate, setActionDate] = useState<Dayjs | null>(null);
  const [actionDateError, setActionDateError] = useState<boolean>(false);

  // Check response
  const [checkResponse, setCheckResponse] =
    useState<MembershipActionCheckResponse | null>(null);
  const [checking, setChecking] = useState<boolean>(false);
  const [checkError, setCheckError] = useState<string | null>(null);

  // Confirmation checkboxes
  const [actionTypeChecked, setActionTypeChecked] = useState<boolean>(false);
  const [affectedGroupsChecked, setAffectedGroupsChecked] = useState<
    Map<number, boolean>
  >(new Map());
  const [newSpdChapterChecked, setNewSpdChapterChecked] =
    useState<boolean>(false);
  const [chapterChangeChecked, setChapterChangeChecked] =
    useState<boolean>(false);

  // Reset all state when modal closes
  const resetState = useCallback(() => {
    setActiveStep('select');
    setChapterId('');
    setChapterError(false);
    setActionDate(null);
    setActionDateError(false);
    setCheckResponse(null);
    setCheckError(null);
    setActionTypeChecked(false);
    setAffectedGroupsChecked(new Map());
    setNewSpdChapterChecked(false);
    setChapterChangeChecked(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  // Check membership action (only for transfer/reinstatement)
  const handleCheck = useCallback(async () => {
    if (!chapterId) {
      setChapterError(true);
      return;
    }
    if (!actionDate) {
      setActionDateError(true);
      return;
    }

    setChapterError(false);
    setActionDateError(false);
    setChecking(true);
    setCheckError(null);

    try {
      const request: MembershipActionRequest = {
        action_type: actionType as 'transfer' | 'reinstatement',
        local_chapter_id: chapterId,
        date: actionDate.format('YYYY-MM-DD'),
      };

      const response = await checkMembershipAction(userId, request);
      setCheckResponse(response);

      if (response.is_valid) {
        setActiveStep('confirm');
        // Initialize checkboxes for affected groups
        if (response.affected_groups && response.affected_groups.length > 0) {
          const initialChecked = new Map<number, boolean>();
          response.affected_groups.forEach((_, index) => {
            initialChecked.set(index, false);
          });
          setAffectedGroupsChecked(initialChecked);
        }
      } else {
        setActiveStep('check');
        // Clear checkError since we'll display errors from response.errors
        setCheckError(null);
      }
    } catch (error: any) {
      setCheckError(
        error?.message || 'Failed to check membership action. Please try again.'
      );
      setActiveStep('check');
    } finally {
      setChecking(false);
    }
  }, [chapterId, actionDate, userId, actionType]);

  // Execute membership action (for transfer/reinstatement)
  const { mutate: executeAction, isPending: isExecuting } = useApiSend(
    (data: { userId: string; request: MembershipActionRequest }) =>
      executeMembershipAction(data.userId, data.request),
    () => {
      setActiveStep('success');
      if (onSuccess) {
        onSuccess();
      }
      // Auto-close after a delay
      setTimeout(() => {
        handleClose();
      }, 2000);
    },
    (error: any) => {
      setCheckError(
        error?.message ||
          'Failed to execute membership action. Please try again.'
      );
    }
  );

  // Execute initiation (for initiation)
  const { mutate: executeInitiation, isPending: isInitiating } = useApiSend(
    (data: { userId: string; request: InitiateUserRequest }) =>
      initiateUser(data.userId, data.request),
    () => {
      setActiveStep('success');
      if (onSuccess) {
        onSuccess();
      }
      // Auto-close after a delay
      setTimeout(() => {
        handleClose();
      }, 2000);
    },
    (error: any) => {
      setCheckError(
        error?.message || 'Failed to initiate member. Please try again.'
      );
    }
  );

  const handleExecute = useCallback(() => {
    if (!chapterId || !actionDate) {
      return;
    }

    if (actionType === 'initiation') {
      const request: InitiateUserRequest = {
        initiation_date: actionDate.format('YYYY-MM-DD'),
        local_chapter_id: chapterId,
      };
      executeInitiation({ userId, request });
    } else {
      const request: MembershipActionRequest = {
        action_type: actionType as 'transfer' | 'reinstatement',
        local_chapter_id: chapterId,
        date: actionDate.format('YYYY-MM-DD'),
      };
      executeAction({ userId, request });
    }
  }, [
    chapterId,
    actionDate,
    actionType,
    userId,
    executeAction,
    executeInitiation,
  ]);

  // Check if all required checkboxes are checked
  const allCheckboxesChecked = useMemo(() => {
    if (!checkResponse) return false;

    // Action type checkbox must be checked
    if (!actionTypeChecked) return false;

    // All affected groups must be checked
    if (
      checkResponse.affected_groups &&
      checkResponse.affected_groups.length > 0
    ) {
      for (let i = 0; i < checkResponse.affected_groups.length; i++) {
        if (!affectedGroupsChecked.get(i)) {
          return false;
        }
      }
    }

    // New SPD chapter checkbox must be checked if old_spd_chapter != new_spd_chapter
    // (only show if both exist and are different)
    if (
      checkResponse.new_spd_chapter &&
      checkResponse.old_spd_chapter &&
      checkResponse.old_spd_chapter !== checkResponse.new_spd_chapter &&
      !newSpdChapterChecked
    ) {
      return false;
    }

    // Chapter change checkbox must be checked if old_chapter and new_chapter are present
    if (
      checkResponse.old_chapter &&
      checkResponse.new_chapter &&
      !chapterChangeChecked
    ) {
      return false;
    }

    return true;
  }, [
    actionTypeChecked,
    affectedGroupsChecked,
    newSpdChapterChecked,
    chapterChangeChecked,
    checkResponse,
  ]);

  const handleAffectedGroupToggle = useCallback((index: number) => {
    setAffectedGroupsChecked((prev) => {
      const newMap = new Map(prev);
      newMap.set(index, !prev.get(index));
      return newMap;
    });
  }, []);

  const steps =
    actionType === 'initiation'
      ? ['Select Chapter & Date', 'Complete']
      : ['Select Chapter & Date', 'Review Changes', 'Complete'];

  const getStepIndex = (step: StepType): number => {
    if (actionType === 'initiation') {
      switch (step) {
        case 'select':
          return 0;
        case 'success':
          return 1;
        default:
          return 0;
      }
    } else {
      switch (step) {
        case 'select':
          return 0;
        case 'check':
        case 'confirm':
          return 1;
        case 'success':
          return 2;
        default:
          return 0;
      }
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 'select':
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Select Chapter and Date
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <ChapterSelector
                  chapter={chapterId}
                  spd={null}
                  reportId="member-action-modal"
                  handleChapterSelect={(value) => {
                    setChapterId(value ? value : '');
                    if (value !== null && value !== '') {
                      setChapterError(false);
                    }
                  }}
                  label="Select Chapter"
                />
                {chapterError && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.75 }}
                  >
                    This field is required
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  date={actionDate}
                  label={
                    actionType === 'transfer'
                      ? 'Transfer Date'
                      : actionType === 'reinstatement'
                        ? 'Reinstatement Date'
                        : 'Initiation Date'
                  }
                  minDate={
                    actionType === 'initiation'
                      ? dayjs('2025-03-01')
                      : undefined
                  }
                  maxDate={
                    actionType === 'initiation' ? dayjs('2026-03-01') : dayjs()
                  }
                  handleDateChange={(date) => {
                    setActionDate(date);
                    if (date) {
                      setActionDateError(false);
                    }
                  }}
                  handleErrorUpdate={setActionDateError}
                  extraError={actionDateError}
                  extraErrorMessage={
                    actionDateError ? 'This field is required' : ''
                  }
                  disableFuture={actionType !== 'initiation'}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 'check':
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Validation Results
            </Typography>
            {checkResponse &&
              !checkResponse.is_valid &&
              checkResponse.errors && (
                <Box sx={{ mt: 2 }}>
                  {checkResponse.errors.map((error, index) => (
                    <Alert
                      key={index}
                      severity="error"
                      sx={{
                        mb: 1,
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        '& .MuiAlert-icon': {
                          color: '#c62828',
                        },
                      }}
                    >
                      {error}
                    </Alert>
                  ))}
                </Box>
              )}
            {!checkResponse && checkError && (
              <Alert
                severity="error"
                sx={{
                  mt: 2,
                  backgroundColor: '#ffebee',
                  color: '#c62828',
                  '& .MuiAlert-icon': {
                    color: '#c62828',
                  },
                }}
              >
                {checkError}
              </Alert>
            )}
          </Box>
        );

      case 'confirm':
        if (!checkResponse) return null;

        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Confirm Changes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please review and confirm the following changes that will occur:
            </Typography>

            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Action Type:
                    </Typography>
                    <Chip
                      label={
                        checkResponse.action_type === 'transfer'
                          ? 'Transfer'
                          : 'Reinstatement'
                      }
                      color={
                        checkResponse.action_type === 'transfer'
                          ? 'primary'
                          : 'secondary'
                      }
                      size="small"
                    />
                  </Grid>

                  {checkResponse.affected_groups &&
                    checkResponse.affected_groups.length > 0 && (
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          gutterBottom
                        >
                          Affected Groups:
                        </Typography>
                        {checkResponse.affected_groups.map((group, index) => (
                          <Box key={index} sx={{ mb: 1 }}>
                            <Typography variant="body2">
                              {group.group_name} - {group.role_name}
                            </Typography>
                          </Box>
                        ))}
                      </Grid>
                    )}

                  {checkResponse.old_chapter && checkResponse.new_chapter && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        Chapter Change:
                      </Typography>
                      <Typography variant="body2">
                        {checkResponse.old_chapter === checkResponse.new_chapter
                          ? `Reinstating into ${checkResponse.new_chapter}`
                          : `Moving from ${checkResponse.old_chapter} to ${checkResponse.new_chapter}`}
                      </Typography>
                    </Grid>
                  )}

                  {checkResponse.new_spd_chapter &&
                    checkResponse.old_spd_chapter &&
                    checkResponse.old_spd_chapter !==
                      checkResponse.new_spd_chapter && (
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          gutterBottom
                        >
                          State/Province/District (S/P/D) Chapter Change:
                        </Typography>
                        <Typography variant="body2">
                          {checkResponse.new_spd_chapter}
                        </Typography>
                      </Grid>
                    )}
                </Grid>
              </CardContent>
            </Card>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Please confirm your understanding:
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={actionTypeChecked}
                    onChange={(e) => setActionTypeChecked(e.target.checked)}
                    color="primary"
                  />
                }
                label={`I understand this is a ${checkResponse.action_type === 'transfer' ? 'transfer' : 'reinstatement'} action`}
                sx={{ display: 'block', mb: 1 }}
              />

              {checkResponse.affected_groups &&
                checkResponse.affected_groups.map((group, index) => (
                  <FormControlLabel
                    key={index}
                    control={
                      <Checkbox
                        checked={affectedGroupsChecked.get(index) || false}
                        onChange={() => handleAffectedGroupToggle(index)}
                        color="primary"
                      />
                    }
                    label={`I understand I will be removing the member from ${group.group_name} (${group.role_name})`}
                    sx={{ display: 'block', mb: 1 }}
                  />
                ))}

              {checkResponse.old_chapter && checkResponse.new_chapter && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={chapterChangeChecked}
                      onChange={(e) =>
                        setChapterChangeChecked(e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label={
                    checkResponse.action_type === 'reinstatement' &&
                    checkResponse.old_chapter === checkResponse.new_chapter
                      ? `I understand I will be reinstating the member into ${checkResponse.new_chapter}`
                      : `I understand I will be moving the member from ${checkResponse.old_chapter} to ${checkResponse.new_chapter}`
                  }
                  sx={{ display: 'block', mb: 1 }}
                />
              )}

              {checkResponse.new_spd_chapter &&
                checkResponse.old_spd_chapter &&
                checkResponse.old_spd_chapter !==
                  checkResponse.new_spd_chapter && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={newSpdChapterChecked}
                        onChange={(e) =>
                          setNewSpdChapterChecked(e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label={`I understand the new S/P/D chapter will be ${checkResponse.new_spd_chapter}`}
                    sx={{ display: 'block', mb: 1 }}
                  />
                )}
            </Box>
          </Box>
        );

      case 'success':
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {actionType === 'transfer'
                ? 'Transfer Completed Successfully!'
                : actionType === 'reinstatement'
                  ? 'Reinstatement Completed Successfully!'
                  : 'Initiation Completed Successfully!'}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  const handleBack = () => {
    if (activeStep === 'check' || activeStep === 'confirm') {
      setActiveStep('select');
      setCheckError(null);
      setCheckResponse(null);
    }
  };

  const handleNext = () => {
    if (activeStep === 'select') {
      // For initiation, skip check and go straight to execution
      if (actionType === 'initiation') {
        handleExecute();
      } else {
        handleCheck();
      }
    }
  };

  const canGoBack =
    (activeStep === 'check' || activeStep === 'confirm') &&
    actionType !== 'initiation';
  const isNextDisabled =
    (activeStep === 'select' &&
      (!chapterId ||
        !actionDate ||
        checking ||
        (actionType !== 'initiation' && checking))) ||
    (activeStep === 'confirm' && !allCheckboxesChecked) ||
    (activeStep === 'select' &&
      actionType === 'initiation' &&
      (isInitiating || isExecuting));

  const getNextButtonText = () => {
    switch (activeStep) {
      case 'select':
        return actionType === 'initiation' ? 'Initiate' : 'Check';
      case 'confirm':
        return actionType === 'transfer' ? 'Transfer' : 'Reinstate';
      default:
        return 'Next';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' },
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          {actionType === 'transfer'
            ? 'Transfer Member'
            : actionType === 'reinstatement'
              ? 'Reinstate Member'
              : 'Initiate Member'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={getStepIndex(activeStep)} sx={{ mb: 3, mt: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderStepContent()}
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={handleClose}
          disabled={checking || isExecuting || isInitiating}
        >
          Cancel
        </Button>
        <Box>
          {canGoBack && (
            <Button
              onClick={handleBack}
              sx={{ mr: 1 }}
              disabled={checking || isExecuting || isInitiating}
            >
              Back
            </Button>
          )}
          {activeStep === 'select' && (
            <LoadingButton
              onClick={handleNext}
              variant="contained"
              loading={checking || isInitiating}
              disabled={isNextDisabled}
            >
              {getNextButtonText()}
            </LoadingButton>
          )}
          {activeStep === 'confirm' && (
            <LoadingButton
              onClick={handleExecute}
              variant="contained"
              loading={isExecuting}
              disabled={isNextDisabled || !allCheckboxesChecked}
            >
              {getNextButtonText()}
            </LoadingButton>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default MemberActionModal;
